/**
 * Google Tag Manager Event Tracking Utilities
 *
 * Provides functions to track user interactions and events in GTM.
 * All events are pushed to the GTM dataLayer for processing.
 */

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  eventCategory: string,
  eventLabel: string,
  additionalData?: Record<string, unknown>
): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: eventName,
    event_category: eventCategory,
    event_label: eventLabel,
    ...additionalData,
  });
}

/**
 * Track a page view (used by GTMPageViewTracker)
 */
export function trackPageView(
  path: string,
  additionalData?: Record<string, unknown>
): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'page_view',
    page_path: path,
    page_title: document.title,
    page_location: window.location.href,
    ...additionalData,
  });
}

/**
 * Track registration funnel events
 */
export function trackRegisterPageView(additionalData?: Record<string, unknown>): void {
  trackPageView('/register', {
    page_section: 'registration',
    ...additionalData,
  });
}

/**
 * Track when user lands on register page - includes referrer and planId for attribution
 */
export function trackRegisterLanding(data: {
  planId?: string | null;
  referrer?: string;
  fromPricing?: boolean;
  productLine?: string;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'register_landing',
    page_path: '/register',
    plan_id: data.planId || undefined,
    referrer: data.referrer || document.referrer || undefined,
    from_pricing: data.fromPricing ?? (document.referrer?.includes('/pricing') ?? false),
    product_line: data.productLine || undefined,
  });
}

/**
 * Track registration step progression - for drop-off analysis
 */
export function trackRegisterStep(step: string, stepNumber: number, totalSteps: number): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'register_step',
    step_name: step,
    step_number: stepNumber,
    total_steps: totalSteps,
    page_path: '/register',
  });
}

/**
 * Track when user starts filling the registration form (first field focus)
 */
export function trackRegisterFormStarted(): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'register_form_started',
    page_path: '/register',
  });
}

/**
 * Track registration form submission (success or failure)
 */
export function trackRegisterSubmit(success: boolean, planId?: string | null): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'register_submit',
    success,
    plan_id: planId || undefined,
    page_path: '/register',
  });
}

/**
 * Track plan selection on finish-signup page
 */
export function trackPlanSelected(planId: string, planName: string, fromPricing?: boolean): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'plan_selected',
    plan_id: planId,
    plan_name: planName,
    from_pricing: fromPricing ?? false,
    page_path: '/finish-signup',
  });
}

/**
 * Track finish-signup page view (plan selection; payment is on Stripe Checkout).
 */
export function trackFinishSignupView(data: {
  funnelStage: 'plan_selection' | 'pre_checkout_redirect';
  planId?: string | null;
  fromPricing?: boolean;
  recommendedTier?: string;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'finish_signup_view',
    funnel_stage: data.funnelStage,
    plan_id: data.planId || undefined,
    from_pricing: data.fromPricing ?? false,
    recommended_tier: data.recommendedTier || undefined,
    page_path: '/finish-signup',
  });
}

/** Onboarding questionnaire step — no PII, sport IDs only (no custom labels). */
export function trackOnboardingQuestionnaireStep(data: {
  stepId: string;
  stepNumber: number;
  totalSteps: number;
  sportIds?: string[];
  operationType?: string;
  flow?: 'questionnaire' | 'pricing_fast_track';
  sizeBand?: string;
  operatingRole?: string;
  offerings?: string[];
  courtCountBand?: string;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'onboarding_questionnaire_step',
    step_id: data.stepId,
    step_number: data.stepNumber,
    total_steps: data.totalSteps,
    sport_ids: data.sportIds?.length ? data.sportIds : undefined,
    operation_type: data.operationType || undefined,
    flow: data.flow || undefined,
    size_band: data.sizeBand || undefined,
    operating_role: data.operatingRole || undefined,
    offerings: data.offerings?.length ? data.offerings : undefined,
    court_count_band: data.courtCountBand || undefined,
    page_path: '/academy-setup',
  });
}

export function trackOnboardingOperationTypeSelected(operationType: string): void {
  trackEvent('onboarding_operation_type_selected', 'Onboarding', operationType, {
    operation_type: operationType,
    page_path: '/academy-setup',
  });
}

export function trackOnboardingQuestionnaireCompleted(data: {
  operationType: string;
  recommendedTier?: string;
  recommendedClubTier?: string;
}): void {
  trackEvent('onboarding_questionnaire_completed', 'Onboarding', data.operationType, {
    operation_type: data.operationType,
    recommended_tier: data.recommendedTier || undefined,
    recommended_club_tier: data.recommendedClubTier || undefined,
    page_path: '/academy-setup',
  });
}

export function trackOnboardingPlanSelected(data: {
  productLine: string;
  selectedTier: string;
  recommendedTier?: string;
  matchedRecommendation?: boolean;
  source: 'questionnaire' | 'pricing';
}): void {
  trackEvent('onboarding_plan_selected', 'Onboarding', data.selectedTier, {
    product_line: data.productLine,
    selected_tier: data.selectedTier,
    recommended_tier: data.recommendedTier || undefined,
    matched_recommendation: data.matchedRecommendation ?? undefined,
    source: data.source,
    page_path: '/academy-setup',
  });
}

export function trackOnboardingFastTrackStarted(data: {
  productLine: string;
  academyTier?: string;
  clubTier?: string;
  billingInterval: string;
}): void {
  trackEvent('onboarding_fast_track_started', 'Onboarding', data.productLine, {
    product_line: data.productLine,
    academy_tier: data.academyTier || undefined,
    club_tier: data.clubTier || undefined,
    billing_interval: data.billingInterval,
    page_path: '/academy-setup',
  });
}

export function trackOnboardingFastTrackChangePlan(): void {
  trackEvent('onboarding_fast_track_change_plan', 'Onboarding', 'change_plan', {
    page_path: '/academy-setup',
  });
}

export function trackCheckoutStarted(data: {
  subscriptionType: 'academy' | 'club' | 'combined';
  academyTier?: string;
  clubTier?: string;
  billingInterval: string;
  source?: string;
}): void {
  trackEvent('checkout_started', 'Checkout', data.subscriptionType, {
    subscription_type: data.subscriptionType,
    academy_tier: data.academyTier || undefined,
    club_tier: data.clubTier || undefined,
    billing_interval: data.billingInterval,
    source: data.source || undefined,
    page_path: '/finish-signup',
  });
}

export function trackOnboardingPlanRecommendation(data: {
  recommendedTier: string;
  selectedTier?: string;
  reasonCodes: string[];
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'onboarding_plan_recommendation',
    recommended_tier: data.recommendedTier,
    selected_tier: data.selectedTier || undefined,
    reason_codes: data.reasonCodes.length ? data.reasonCodes : undefined,
    page_path: '/academy-setup',
  });
}

export function trackStarterLimitationsAcknowledged(reasonCodes: string[]): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'starter_limitations_acknowledged',
    reason_codes: reasonCodes.length ? reasonCodes : undefined,
    page_path: window.location.pathname,
  });
}

export function trackPostSubscriptionOnboardingStep(data: {
  stepId: string;
  stepNumber: number;
  totalSteps: number;
  skipped?: boolean;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'post_subscription_onboarding_step',
    step_id: data.stepId,
    step_number: data.stepNumber,
    total_steps: data.totalSteps,
    skipped: data.skipped ?? false,
    page_path: '/admin/onboarding',
  });
}

export function trackPostSubscriptionOnboardingComplete(data: {
  skipped: boolean;
  completedStepIds: string[];
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'post_subscription_onboarding_complete',
    skipped: data.skipped,
    completed_step_ids: data.completedStepIds.length ? data.completedStepIds : undefined,
    page_path: '/admin/onboarding',
  });
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}
